import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Colaborador, ColaboradorAtualizacaoPayload, ColaboradorPayload, GestorResumo, StatusOnlineFundido } from 'src/app/pages/pessoas/models/colaborador.model';
import { DispositivoColaborador } from 'src/app/pages/pessoas/models/dispositivo.model';
import { Page } from 'src/app/pages/pessoas/models/page.model';
import { environment } from 'src/environments/environment';
import { ApiResponse } from 'src/app/core/models/api-response.model';
import { unwrapConteudo } from '../utils/api.utils';

export interface AoVivoResponse {
  url?: string;
  streamUrl?: string;
  redirectUrl?: string;
}

export interface ContadoresStatusColaboradores {
  online: number;
  ausente: number;
  offline: number;
  total: number;
}

export interface UsuarioResposta {
  id: number;
  nome?: string;
  sobrenome?: string;
  nomeCompleto?: string;
  identificador: string;
  email?: string;
  cargo?: string;
  perfil: 'GESTOR' | 'COLABORADOR';
  monitorado: boolean;
  avatarUrl?: string | null;
  statusOnline?: 'ATIVO' | 'PAUSA' | 'AUSENTE';
  ultimoHeartbeat?: string;
  ativo?: boolean;
}

export interface PaginaColaboradoresResposta {
  conteudo: Colaborador[];
  contadores: ContadoresStatusColaboradores;
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
}

@Injectable({
  providedIn: 'root'
})
export class ColaboradoresApiService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  listar(pagina: number, tamanho: number, filtro?: string, gestorId?: number): Observable<Page<Colaborador>> {
    let params = new HttpParams()
      .set('page', pagina)
      .set('size', tamanho);

    if (filtro?.trim()) {
      params = params.set('filtro', filtro.trim());
    }

    if (gestorId) {
      params = params.set('gestorId', gestorId);
    }

    // v1 retorna SpringPage<T> — adapter para Page<T> (nomenclatura Java do fed)
    return this.http.get<any>(`${this.baseUrl}/api/v1/colaboradores`, { params }).pipe(
      map((page) => {
        const conteudo = ((page.content ?? page.conteudo ?? []) as any[]).map((c) =>
          this.normalizeColaborador(c)
        );
        return {
          conteudo,
          pagina:        page.number  ?? page.pagina        ?? pagina,
          tamanhoPagina: page.size    ?? page.tamanhoPagina ?? tamanho,
          totalElementos: page.totalElements ?? page.totalElementos ?? 0,
          totalPaginas:  page.totalPages ?? page.totalPaginas ?? 0,
          primeira:      page.first   ?? page.primeira,
          ultima:        page.last    ?? page.ultima,
        } as Page<Colaborador>;
      })
    );
  }

  listarColaboradoresDoTime(
    timeId: number,
    page: number,
    size: number,
    filtro?: string,
    status?: 'ATIVO' | 'PAUSA' | 'AUSENTE'
  ): Observable<PaginaColaboradoresResposta> {
    let params = new HttpParams()
      .set('page', page)
      .set('size', size);

    if (filtro?.trim()) {
      params = params.set('filtro', filtro.trim());
    }

    if (status) {
      params = params.set('status', status);
    }

    // v1: GET /api/v1/times/{timeId}/colaboradores — SpringPage<ColaboradorListaItemResponseDto>
    return this.http
      .get<any>(
        `${this.baseUrl}/api/v1/times/${timeId}/colaboradores`,
        { params }
      )
      .pipe(
        map((springPage) => {
          const conteudo = ((springPage.content ?? springPage.conteudo ?? []) as any[]).map((c) =>
            this.normalizeColaborador(c)
          );
          // N3 RESOLVIDO (@Shuri 2026-08-28, achado @Miles): backend passou a
          // devolver o bloco `contadores` (mesmo shape do endpoint cross-team
          // GET /api/v1/times/colaboradores) e a aceitar `?status=` de verdade
          // neste endpoint. Antes o bloco não existia e os 3 buckets vinham
          // hardcoded 0 — a UI escondia os chips em modo "1 time" pra não
          // mentir. Mapeamento idêntico ao usado em
          // `listarColaboradoresDeTodosOsTimes` (Opção B com fallback A),
          // reaproveitado aqui por design.
          //
          // Semântica preservada: `contadores` SEMPRE reflete o total do time
          // SEM o filtro `?status=` — só `content`/`conteudo` é filtrado. Não
          // recalcular os buckets a partir de `conteudo` (que já vem filtrado
          // quando `status` está setado): isso zeraria todo chip menos o
          // selecionado.
          //
          // BUG-N2 RESOLVIDO (Marcos 2026-08-28) — `totalElementsFiltrado`
          // (antes chamado `totalReal`, e usado direto em `contadores.total`)
          // é o `totalElements` do SpringPage, que é PAGINAÇÃO — precisa
          // refletir o total FILTRADO, porque `conteudo` também vem filtrado
          // (é o que faz "página 2 de 3" bater quando só ATIVO está
          // selecionado). O comentário antigo dizia que `totalElements` era
          // "a contagem do banco pro time inteiro" — essa premissa deixou de
          // valer: o backend passou a filtrar `totalElements` por `?status=`
          // também. Usar o mesmo número pros dois (paginação E o chip
          // "Todos") zerava o chip com PAUSA selecionado (Todos=0 na tela,
          // print do Marcos: time 1551, nenhum chip=10, ATIVO=1, PAUSA=0).
          // `contadores.total` agora vem de `cb.totalElementos`, o mesmo
          // campo que `listarColaboradoresDeTodosOsTimes` já usa — o bloco
          // `contadores` é invariante ao filtro por contrato (comentário
          // acima), é exatamente pra isso que ele existe.
          const totalElementsFiltrado = springPage.totalElements ?? conteudo.length;
          const cb = springPage.contadores ?? ({} as ContadoresDtoApi);
          const contadores: ContadoresStatusColaboradores = {
            online:  cb.totalOnline  ?? cb.ativo   ?? 0,
            ausente: cb.totalOcioso  ?? cb.pausa   ?? 0,
            offline: cb.totalOffline ?? cb.ausente ?? 0,
            total:   cb.totalElementos ?? cb.totalGeral ?? cb.total ?? conteudo.length,
          };
          return {
            conteudo,
            contadores,
            totalElements: totalElementsFiltrado,
            totalPages: springPage.totalPages ?? 1,
            page: springPage.number ?? page,
            size: springPage.size ?? size
          } as PaginaColaboradoresResposta;
        })
      );
  }

  listarColaboradoresDeTodosOsTimes(
    page: number,
    size: number,
    filtro?: string,
    status?: 'ATIVO' | 'PAUSA' | 'AUSENTE'
  ): Observable<PaginaColaboradoresResposta> {
    const filtroBusca = filtro?.toLowerCase().trim() ?? '';

    // Monta query params — status é server-side (Shuri 2026-07-31)
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }

    // v1: GET /api/v1/times/colaboradores — { conteudo: TimeComColaboradoresDto[], contadores: ContadoresDtoApi }
    // Agregado cross-time sem paginação server-side. Adapter achata para PaginaColaboradoresResposta.
    return this.http
      .get<{ conteudo: TimeComColaboradoresDtoApi[]; contadores: ContadoresDtoApi }>(
        `${this.baseUrl}/api/v1/times/colaboradores`,
        { params }
      )
      .pipe(
        map((response) => {
          // Achatar e aplicar filtro de texto (busca por nome continua client-side)
          const todos: Colaborador[] = [];

          for (const time of (response.conteudo ?? [])) {
            for (const c of (time.colaboradores ?? [])) {
              if (filtroBusca && !(c.nome ?? '').toLowerCase().includes(filtroBusca)) continue;
              todos.push(this.normalizeColaborador({
                id: c.id,
                nome: c.nome,
                cargo: c.cargo ?? null,
                avatarUrl: c.avatarUrl ?? null,
                nomeTime: time.timeNome,
                statusOnline: c.statusOnline ?? undefined,  // preserva 'ATIVO'|'PAUSA'|'AUSENTE' de Shuri
                // Item #9-fed 2026-08-12: propaga campos multi-dispositivo pro
                // card do modo "Todos os times" (paridade com o modo filtrado).
                // Backend ja devolve estes campos no agregado — antes desta
                // atualizacao a fed dropava porque so pegava 6 fields fixos.
                dispositivos: (c.dispositivos ?? undefined) as Colaborador['dispositivos'],
                horasHojeFormatadas: c.horasHojeFormatadas ?? null,
              } as unknown as Colaborador));
            }
          }

          // Paginação client-side sobre o agregado (endpoint v1 sem paginação server-side)
          const start = page * size;
          const paginado = todos.slice(start, start + size);

          // Contadores vêm do backend (server-side — Shuri 2026-07-31).
          // Defensivo para Opção B (paridade Java: totalOnline/totalOcioso/totalOffline/totalGeral)
          // e Opção A (ativo/pausa/ausente/total). Mapping fed: online=ATIVO, ausente=PAUSA, offline=AUSENTE.
          const cb = response.contadores ?? ({} as ContadoresDtoApi);
          const contadores: ContadoresStatusColaboradores = {
            online:  cb.totalOnline  ?? cb.ativo   ?? 0,
            ausente: cb.totalOcioso  ?? cb.pausa   ?? 0,
            offline: cb.totalOffline ?? cb.ausente ?? 0,
            total:   cb.totalElementos ?? cb.totalGeral   ?? cb.total   ?? 0,
          };

          return {
            conteudo: paginado,
            contadores,
            totalElements: todos.length,
            totalPages: Math.ceil(todos.length / size) || 1,
            page,
            size
          } as PaginaColaboradoresResposta;
        })
      );
  }

  obterPorId(id: number): Observable<Colaborador> {
    // v1: GET /api/v1/colaboradores/id/{id} — ColaboradorDetalhesResponseDto (slim ~14 campos)
    // Renames v1: gestorDireto → gestor, departamentoResumo → departamento.
    // Campos omitidos intencionalmente (telefone, emailPessoal, dataContratacao,
    // times, jornadaHoras, menuVisivel) — formulário de edição trata como undefined.
    //
    // D4 (2026-08-26): resposta ganha `statusOnline` fundido (spec §4.6) —
    // mapeado explicitamente pra `statusOnlineFundido` (campo NOVO, distinto
    // do `statusOnline` flat/LIST-only que `Colaborador` já tinha). `...dto`
    // sozinho vazaria o objeto bruto sob a chave antiga (tipo incompatível,
    // string vs objeto) — por isso a chave `statusOnline` é explicitamente
    // zerada aqui antes do spread valer.
    //
    // Fix @Peter 2026-07-23: Node envolve resposta em { conteudo } matching Java
    // (fix @Shuri wrapper — parity-run 2026-07-23). Precisa unwrapConteudo pra
    // dto ficar sendo o objeto direto (senao dto.id vira undefined → isEdicao false
    // → modal abre em modo criar em vez de editar).
    return this.http
      .get<ApiResponse<ColaboradorDetalhesRespostaApi>>(`${this.baseUrl}/api/v1/colaboradores/id/${id}`)
      .pipe(
        map((response) => unwrapConteudo(response)),
        map((dto) => this.normalizeColaborador({
          ...dto,
          gestor: dto.gestorDireto ? {
            id: dto.gestorDireto.id,
            nome: dto.gestorDireto.nome,
            avatarUrl: dto.gestorDireto.avatarUrl ?? null
          } : null,
          departamento: dto.departamentoResumo ? {
            id: dto.departamentoResumo.id,
            nome: dto.departamentoResumo.nome
          } : null,
          statusOnline: undefined,
          statusOnlineFundido: dto.statusOnline ?? null,
        } as unknown as Colaborador))
      );
  }

  obterPorIdentificador(identificador: string): Observable<Colaborador> {
    const encoded = encodeURIComponent(identificador);
    return this.http
      .get<ApiResponse<Colaborador>>(
        `${this.baseUrl}/api/v1/colaboradores/${encoded}`
      )
      .pipe(map((response) => this.normalizeColaborador(unwrapConteudo(response))));
  }

  criar(payload: ColaboradorPayload, avatarFile?: File): Observable<Colaborador> {
    const formData = this.montarFormData(payload, avatarFile);
    return this.http.post<ApiResponse<Colaborador>>(`${this.baseUrl}/api/v1/colaboradores`, formData).pipe(
      map((response) => this.normalizeColaborador(unwrapConteudo(response)))
    );
  }

  criarLote(colaboradores: any[]): Observable<{ criados: number; falhas: number; erros: string[] }> {
    return this.http.post<{ criados: number; falhas: number; erros: string[] }>(
      `${this.baseUrl}/api/v1/colaboradores/lote`,
      colaboradores
    );
  }

  atualizar(id: number, payload: ColaboradorAtualizacaoPayload, avatarFile?: File): Observable<Colaborador> {
    const formData = this.montarFormData(payload, avatarFile);
    return this.http.patch<ApiResponse<Colaborador>>(`${this.baseUrl}/api/v1/colaboradores/${id}`, formData).pipe(
      map((response) => this.normalizeColaborador(unwrapConteudo(response)))
    );
  }

  excluir(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/api/v1/colaboradores/${id}`);
  }

  atualizarMenuVisivel(id: number, menuVisivel: boolean): Observable<void> {
    // v1: PATCH /api/v1/usuarios/{id}/menu-visivel — body: { valor: boolean }
    return this.http.patch<void>(`${this.baseUrl}/api/v1/usuarios/${id}/menu-visivel`, { valor: menuVisivel });
  }

  listarDispositivos(id: number): Observable<DispositivoColaborador[]> {
    return this.http.get<ApiResponse<DispositivoColaborador[]>>(
      `${this.baseUrl}/api/v1/colaboradores/${id}/dispositivos`
    ).pipe(map((response) => unwrapConteudo(response) ?? []));
  }

  /**
   * Atualiza configs per-dispositivo do Agent (Fase 2b — spec 2026-08-11 §6.2).
   *
   *   PATCH /api/v1/colaboradores/{colaboradorId}/dispositivos/{agenteId}/configs
   *     body: { menuVisivelNoAgent?, coletarReunioes?, autoUpdate? }
   *
   * Endpoint materializado por @Shuri após V119 (colunas persistidas em
   * `agentes`). Retorna o DTO atualizado (backend faz merge parcial — só as
   * chaves enviadas são alteradas).
   */
  atualizarConfigsDispositivo(
    colaboradorId: number,
    agenteId: number,
    configs: Partial<{
      menuVisivelNoAgent: boolean;
      coletarReunioes: boolean;
      autoUpdate: boolean;
    }>
  ): Observable<DispositivoColaborador> {
    return this.http.patch<ApiResponse<DispositivoColaborador>>(
      `${this.baseUrl}/api/v1/colaboradores/${colaboradorId}/dispositivos/${agenteId}/configs`,
      configs
    ).pipe(map((response) => unwrapConteudo(response)));
  }

  listarGestores(
    pagina: number,
    tamanho: number,
    filtro?: string,
    departamentoId?: number | null
  ): Observable<Page<GestorResumo>> {
    // v1: GET /api/v1/colaboradores?perfil=GESTOR&page=X&size=Y — SpringPage<ColaboradorListaItemResponseDto>
    let params = new HttpParams()
      .set('perfil', 'GESTOR')
      .set('page', pagina)
      .set('size', tamanho);

    if (filtro?.trim()) {
      params = params.set('filtro', filtro.trim());
    }

    if (departamentoId !== null && departamentoId !== undefined) {
      params = params.set('departamentoId', departamentoId);
    }

    return this.http
      .get<any>(`${this.baseUrl}/api/v1/colaboradores`, { params })
      .pipe(
        map((springPage) => {
          // SpringPage → Page<GestorResumo> (shape que o fed usa)
          const conteudo: GestorResumo[] = ((springPage.content ?? springPage.conteudo ?? []) as any[]).map((c) => ({
            id: c.id,
            nome: c.nome ?? c.nomeCompleto,
            sobrenome: c.sobrenome,
            identificador: c.identificador,
            emailCorporativo: c.email,
            cargo: c.cargo,
            quantidadeColaboradores: 0,
            avatarUrl: this.resolveAvatarUrl(c.avatarUrl)
          } as GestorResumo));

          return {
            conteudo,
            pagina: springPage.number ?? pagina,
            tamanhoPagina: springPage.size ?? tamanho,
            totalElementos: springPage.totalElements ?? conteudo.length,
            totalPaginas: springPage.totalPages ?? 1,
            primeira: springPage.first ?? true,
            ultima: springPage.last ?? true
          } as Page<GestorResumo>;
        })
      );
  }

  private montarFormData(
    dados: ColaboradorPayload | ColaboradorAtualizacaoPayload,
    avatarFile?: File
  ): FormData {
    const formData = new FormData();
    formData.append('dados', JSON.stringify(dados));
    if (avatarFile) {
      formData.append('avatar', avatarFile, avatarFile.name);
    }
    return formData;
  }

  private normalizeColaborador(colaborador: Colaborador): Colaborador {
    return {
      ...colaborador,
      avatarUrl: this.resolveAvatarUrl(colaborador.avatarUrl)
    };
  }

  private resolveAvatarUrl(avatarUrl?: string | null): string | null {
    if (!avatarUrl) return null;
    if (avatarUrl.startsWith('http://') || avatarUrl.startsWith('https://')) {
      return avatarUrl;
    }
    // Normaliza paths legados de avatar para v1
    let cleaned = avatarUrl.replace('/api/colaboradores/', '/api/colaborador/');
    cleaned = cleaned.replace(/\/api\/colaborador\/(\d+)\/avatar/, '/api/v1/usuarios/$1/avatar');
    const normalized = cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
    return `${this.baseUrl}${normalized}`;
  }

}

// ─── Interfaces privadas de resposta da API v1 ──────────────────────────────

/**
 * Shape do GET /api/v1/colaboradores/id/{id} (ColaboradorDetalhesResponseDto).
 *
 * @Peter 2026-07-23: expandido com campos que a modal de edicao consome
 * (times, jornadaHoras, telefone, emailPessoal, dataContratacao, dataNascimento,
 * estadoCivil, genero, menuVisivel) — Node passou a retornar via @Shuri
 * expandir DTO na mesma data (task #12).
 */
interface ColaboradorDetalhesRespostaApi {
  id: number;
  nome: string;
  sobrenome: string | null;
  nomeCompleto: string | null;
  identificador: string;
  email: string | null;
  emailPessoal: string | null;
  telefone: string | null;
  cargo: string | null;
  avatarUrl: string | null;
  departamentoId: number | null;
  jornadaHoras: number;
  dataContratacao: string | null;
  dataNascimento: string | null;
  estadoCivil: string | null;
  genero: string | null;
  monitoramentoAtivo: boolean;
  podeAcessarPortal: boolean;
  menuVisivel: boolean;
  ativo: boolean;
  gestorDireto: { id: number; nome: string; avatarUrl: string | null } | null;
  departamentoResumo: { id: number; nome: string } | null;
  times: Array<{ id: number; nome: string }>;
  /** D4 — status online fundido (spec §4.6). Opcional: backend pode omitir. */
  statusOnline?: StatusOnlineFundido | null;
}

/**
 * Shape do bloco `contadores` retornado pelo GET /api/v1/times/colaboradores.
 * Defensivo para Opção B (paridade Java) e Opção A — Shuri ainda decide nome final.
 *
 * Opção B (mais provável): totalOnline, totalOcioso, totalOffline, totalGeral
 * Opção A (alternativa):   ativo, pausa, ausente, total
 */
interface ContadoresDtoApi {
  // Opção B — paridade Java (Shuri 2026-07-31: nome canônico do campo total)
  totalElementos?: number;
  totalOnline?: number;
  totalOcioso?: number;
  totalOffline?: number;
  totalGeral?: number;
  // Opção A — alternativa
  total?: number;
  ativo?: number;
  pausa?: number;
  ausente?: number;
}

/** Shape de um time no agregado do GET /api/v1/times/colaboradores. */
interface TimeComColaboradoresDtoApi {
  timeId: number;
  timeNome: string;
  colaboradores: Array<{
    id: number;
    nome: string;
    cargo: string | null;
    avatarUrl: string | null;
    statusOnline?: 'ATIVO' | 'PAUSA' | 'AUSENTE';  // adicionado por Shuri — 2026-07-31
    /**
     * Multi-dispositivo (item #9-fed 2026-08-12). Backend confirmou que o
     * agregado tambem devolve `dispositivos[]` + `horasHojeFormatadas` no
     * mesmo shape do endpoint filtrado por time — necessario pra unificar
     * o layout dos cards (mostrar badges + horas hoje no modo "Todos").
     */
    dispositivos?: Array<{ tipo: import('src/app/pages/pessoas/models/colaborador.model').ColaboradorDispositivoResumo['tipo']; status: 'ATIVO' | 'PAUSA' | 'AUSENTE' }>;
    horasHojeFormatadas?: string | null;
  }>;
}
